import React, { useState, useEffect, useRef, useCallback, Fragment } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { toast } from '../../components/toast/CustomToast'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'
import Modal from '../../components/common/Modal'
import Pagination from '../../components/common/Pagination'
import MonacoEditor from '../../components/common/MonacoEditor'
import HeadersConfig from '../../components/common/HeadersConfig'
import EnvironmentSection from '../../components/common/EnvironmentSection'
import api from '../../utils/api'
import { eventAPI, supplierAPI, setupAPI } from '../../utils/apiHelpers'

const SettingsPage = ({ eventsData, setEventsData }) => {
    const { token } = useAuth();
    const [activeSection, setActiveSection] = useState('events');
    const [searchTerm, setSearchTerm] = useState('');
    const [isEventModalOpen, setIsEventModalOpen] = useState(false);
    const [isSystemModalOpen, setIsSystemModalOpen] = useState(false);
    const [eventFormData, setEventFormData] = useState({
        id: '',
        name: '',
        type: '',
        source: '',
        specversion: '1.0',
        datacontenttype: 'application/json',
        active: true
    });
    const [eventFormError, setEventFormError] = useState('');
    const [editingEvent, setEditingEvent] = useState(null);
    const [eventTypes, setEventTypes] = useState([]);
    const [currentEventType, setCurrentEventType] = useState('');
    const [systemTypesData, setSystemTypesData] = useState([]);
    const [isSystemTypeModalOpen, setIsSystemTypeModalOpen] = useState(false);
    const [systemTypeFormData, setSystemTypeFormData] = useState({ name: '' });
    const [systemTypeFormError, setSystemTypeFormError] = useState('');
    const [editingSystemType, setEditingSystemType] = useState(null);
    const [systemsData, setSystemsData] = useState([]);
    const [systemFormData, setSystemFormData] = useState({ name: '', description: '', system_type_id: null, supplier_id: null });
    const [systemFormError, setSystemFormError] = useState('');
    const [editingSystem, setEditingSystem] = useState(null);
    const [suppliersData, setSuppliersData] = useState([]);
    const [isSupplierModalOpen, setIsSupplierModalOpen] = useState(false);
    const [supplierFormData, setSupplierFormData] = useState({ name: '', logo_url: '' });
    const [supplierFormError, setSupplierFormError] = useState('');
    const [editingSupplier, setEditingSupplier] = useState(null);
    const [selectedSystemsForSupplier, setSelectedSystemsForSupplier] = useState([]);
    const [logoValid, setLogoValid] = useState(null);
    const [supplierSystemsFilter, setSupplierSystemsFilter] = useState('');
    const [expandedSupplierSystems, setExpandedSupplierSystems] = useState({});
    const [expandedSystemsSuppliers, setExpandedSystemsSuppliers] = useState({});

    const PAGE_SIZE = 10;
    const [eventsPage, setEventsPage] = useState(1);
    const [systemTypesPage, setSystemTypesPage] = useState(1);
    const [systemsPage, setSystemsPage] = useState(1);
    const [suppliersPage, setSuppliersPage] = useState(1);
    const [expandedEventGroups, setExpandedEventGroups] = useState({});

    // Load events and other data from API on component mount
    useEffect(() => {
        const loadData = async () => {
            try {
                const token = localStorage.getItem('token');
                if (token) {
                    // Load events
                    const eventsResponse = await eventAPI.list(1, 100, token);
                    setEventsData(eventsResponse.items || []);

                    // Load system types
                    const systemTypesResponse = await api.get('/systemtypes', {
                        params: { page: 1, page_size: 100 }
                    });
                    setSystemTypesData(systemTypesResponse.data.items || []);

                    // Load systems
                    const systemsResponse = await api.get('/systems', {
                        params: { page: 1, page_size: 100 }
                    });
                    setSystemsData(systemsResponse.data.items || []);

                    // Load suppliers
                    const suppliersResponse = await api.get('/suppliers', {
                        params: { page: 1, page_size: 100 }
                    });
                    setSuppliersData(suppliersResponse.data.items || []);
                }
            } catch (error) {

            }
        };
        loadData();
    }, []);

    const filteredEvents = eventsData.filter(event =>
        event.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.source.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredSystemTypes = systemTypesData.filter(systemType =>
        systemType.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredSystems = systemsData.filter(system =>
        (system.name || system.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (system.supplier && system.supplier.name.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const filteredSuppliers = suppliersData.filter(supplier =>
        supplier.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const paginatedEvents = filteredEvents.slice((eventsPage - 1) * PAGE_SIZE, eventsPage * PAGE_SIZE);
    const paginatedSystemTypes = filteredSystemTypes.slice((systemTypesPage - 1) * PAGE_SIZE, systemTypesPage * PAGE_SIZE);
    const paginatedSystems = filteredSystems.slice((systemsPage - 1) * PAGE_SIZE, systemsPage * PAGE_SIZE);
    const paginatedSuppliers = filteredSuppliers.slice((suppliersPage - 1) * PAGE_SIZE, suppliersPage * PAGE_SIZE);

    const handleAddEvent = async (e) => {
        e.preventDefault();
        setEventFormError('');

        if (!eventFormData.name || !eventFormData.source) {
            setEventFormError('Nome e Source são obrigatórios');
            return;
        }

        if (editingEvent && !eventFormData.type) {
            setEventFormError('Type é obrigatório');
            return;
        }

        if (!editingEvent && eventTypes.length === 0) {
            setEventFormError('Adicione pelo menos um tipo de evento');
            return;
        }

        try {
            if (editingEvent) {
                await eventAPI.update(editingEvent.id, {
                    name: eventFormData.name,
                    type: eventFormData.type,
                    source: eventFormData.source,
                    specversion: eventFormData.specversion,
                    datacontenttype: eventFormData.datacontenttype,
                    active: eventFormData.active
                }, token);

                setEventsData(eventsData.map(event => event.id === editingEvent.id ? { ...event, ...eventFormData } : event));
            } else {
                const newEvents = [];
                for (const type of eventTypes) {
                    const newEvent = await eventAPI.create({
                        name: eventFormData.name,
                        type: type,
                        source: eventFormData.source,
                        specversion: eventFormData.specversion,
                        datacontenttype: eventFormData.datacontenttype,
                        active: eventFormData.active
                    }, token);
                    newEvents.push(newEvent);
                }

                setEventsData([...eventsData, ...newEvents]);
            }

            setEventFormData({
                id: '',
                name: '',
                type: '',
                source: 'cloudevents.n4h.com',
                specversion: '1.0',
                datacontenttype: 'application/json',
                active: true
            });
            setEventTypes([]);
            setCurrentEventType('');
            setEditingEvent(null);
            setIsEventModalOpen(false);

            toast.success(editingEvent ? 'Evento atualizado com sucesso' : 'Eventos criados com sucesso');
        } catch (error) {
            setEventFormError(`Erro ao salvar evento: ${error.message}`);
        }
    };

    const handleRemoveEvent = async (id) => {
        try {
            await eventAPI.delete(id, token);
            setEventsData(eventsData.filter(event => event.id !== id));
            toast.success('Evento deletado com sucesso');
        } catch (error) {
            toast.error(`Erro ao deletar evento: ${error.message}`);
        }
    };

    const openEventModal = (event = null) => {
        if (event) {
            setEditingEvent(event);
            setEventFormData({
                id: event.id,
                name: event.name,
                type: event.type,
                source: event.source,
                specversion: event.specversion,
                datacontenttype: event.datacontenttype,
                active: event.active
            });
            setEventTypes([]);
            setCurrentEventType('');
        } else {
            setEditingEvent(null);
            setEventFormData({
                id: '',
                name: '',
                type: '',
                source: 'cloudevents.n4h.com',
                specversion: '1.0',
                datacontenttype: 'application/json',
                active: true
            });
            setEventTypes([]);
            setCurrentEventType('');
        }
        setEventFormError('');
        setIsEventModalOpen(true);
    };

    const addEventType = () => {
        if (!currentEventType.trim()) {
            setEventFormError('Digite um tipo de evento');
            return;
        }
        if (eventTypes.includes(currentEventType.trim())) {
            setEventFormError('Este tipo de evento já foi adicionado');
            return;
        }
        setEventTypes([...eventTypes, currentEventType.trim()]);
        setCurrentEventType('');
        setEventFormError('');
    };

    const removeEventType = (index) => {
        setEventTypes(eventTypes.filter((_, i) => i !== index));
    };

    const handleAddSystemType = async (e) => {
        e.preventDefault();
        setSystemTypeFormError('');

        if (!systemTypeFormData.name) {
            setSystemTypeFormError('Nome é obrigatório');
            return;
        }

        try {
            let result;
            if (editingSystemType) {
                const response = await api.put(`/systemtypes/${editingSystemType.id}`, { name: systemTypeFormData.name });
                result = response.data;
                setSystemTypesData(systemTypesData.map(st => st.id === result.id ? result : st));
                toast.success('Tipo de Sistema atualizado com sucesso');
            } else {
                const response = await api.post('/systemtypes', { name: systemTypeFormData.name });
                result = response.data;
                setSystemTypesData([...systemTypesData, result]);
                toast.success('Tipo de Sistema criado com sucesso');
            }
            setSystemTypeFormData({ name: '' });
            setIsSystemTypeModalOpen(false);
            setEditingSystemType(null);
        } catch (error) {
            setSystemTypeFormError(`Erro: ${error.message}`);
        }
    };

    const handleRemoveSystemType = async (id) => {
        try {
            await api.delete(`/systemtypes/${id}`);
            setSystemTypesData(systemTypesData.filter(st => st.id !== id));
            toast.success('Tipo de Sistema deletado com sucesso');
        } catch (error) {
            toast.error(`Erro ao deletar tipo de sistema: ${error.message}`);
        }
    };

    const openSystemTypeModal = (systemType = null) => {
        if (systemType) {
            setEditingSystemType(systemType);
            setSystemTypeFormData({ name: systemType.name });
        } else {
            setEditingSystemType(null);
            setSystemTypeFormData({ name: '' });
        }
        setSystemTypeFormError('');
        setIsSystemTypeModalOpen(true);
    };

    const handleAddSystem = async (e) => {
        e.preventDefault();
        setSystemFormError('');

        if (!systemFormData.name) {
            setSystemFormError('Nome é obrigatório');
            return;
        }

        if (!systemFormData.description) {
            setSystemFormError('Descrição é obrigatória');
            return;
        }

        if (!systemFormData.supplier_id) {
            setSystemFormError('Fornecedor é obrigatório');
            return;
        }

        try {
            const body = {
                name: systemFormData.name,
                description: systemFormData.description,
                supplier_id: systemFormData.supplier_id,
                ...(systemFormData.system_type_id && { system_type_id: systemFormData.system_type_id })
            };

            let result;
            if (editingSystem) {
                const response = await api.put(`/systems/${editingSystem.id}`, body);
                result = response.data;
                setSystemsData(systemsData.map(s => s.id === result.id ? result : s));
                toast.success('Sistema atualizado com sucesso');
            } else {
                const response = await api.post('/systems', body);
                result = response.data;
                setSystemsData([...systemsData, result]);
                toast.success('Sistema criado com sucesso');
            }
            setSystemFormData({ name: '', description: '', system_type_id: null, supplier_id: null });
            setIsSystemModalOpen(false);
            setEditingSystem(null);
        } catch (error) {
            setSystemFormError(`Erro: ${error.message}`);
        }
    };

    const openSystemModal = (system = null) => {
        if (system) {
            setEditingSystem(system);
            const resolvedName = system.name || (!system.name && system.description ? system.description : '');
            setSystemFormData({
                name: resolvedName,
                description: system.description || '',
                system_type_id: system.system_type_id || (system.system_type ? system.system_type.id : null),
                supplier_id: system.supplier_id || (system.supplier ? system.supplier.id : null)
            });
        } else {
            setEditingSystem(null);
            setSystemFormData({ name: '', description: '', system_type_id: null, supplier_id: null });
        }
        setSystemFormError('');
        setIsSystemModalOpen(true);
    };

    const handleRemoveSystem = async (id) => {
        try {
            await api.delete(`/systems/${id}`);
            setSystemsData(systemsData.filter(system => system.id !== id));

            // Reload suppliers to update systems list
            const suppliersResponse = await api.get('/suppliers', {
                params: { page: 1, page_size: 100 }
            });
            setSuppliersData(suppliersResponse.data.items || []);

            toast.success('Sistema deletado com sucesso');
        } catch (error) {
            toast.error(`Erro ao deletar sistema: ${error.message}`);
        }
    };

    const handleAddSupplier = async (e) => {
        e.preventDefault();
        setSupplierFormError('');

        if (!supplierFormData.name) {
            setSupplierFormError('Nome é obrigatório');
            return;
        }

        try {
            let newSupplier;
            if (editingSupplier) {
                const response = await api.put(`/suppliers/${editingSupplier.id}`, supplierFormData);
                newSupplier = response.data;
            } else {
                const response = await api.post('/suppliers', supplierFormData);
                newSupplier = response.data;
            }

            // Associate systems if any selected
            if (selectedSystemsForSupplier.length > 0) {
                await api.put(`/suppliers/${newSupplier.id}/systems`, {
                    system_ids: selectedSystemsForSupplier
                });
            }

            // Reload both systems and suppliers to get latest associations
            const systemsResponse = await api.get('/systems', {
                params: { page: 1, page_size: 100 }
            });
            setSystemsData(systemsResponse.data.items || []);

            const suppliersResponse = await api.get('/suppliers', {
                params: { page: 1, page_size: 100 }
            });
            setSuppliersData(suppliersResponse.data.items || []);

            setSupplierFormData({ name: '', logo_url: '' });
            setSelectedSystemsForSupplier([]);
            setEditingSupplier(null);
            setIsSupplierModalOpen(false);
            toast.success(editingSupplier ? 'Fornecedor atualizado com sucesso' : 'Fornecedor criado com sucesso');
        } catch (error) {
            setSupplierFormError(error.response?.data?.detail || `Erro ao salvar fornecedor: ${error.message}`);
        }
    };

    const handleRemoveSupplier = async (id) => {
        try {
            await api.delete(`/suppliers/${id}`);
            setSuppliersData(suppliersData.filter(supplier => supplier.id !== id));
            toast.success('Fornecedor deletado com sucesso');
        } catch (error) {
            toast.error(`Erro ao deletar fornecedor: ${error.message}`);
        }
    };

    const openSupplierModal = (supplier = null) => {
        if (supplier) {
            setEditingSupplier(supplier);
            setSupplierFormData({ name: supplier.name, logo_url: supplier.logo_url || '' });
            setSelectedSystemsForSupplier((supplier.systems || []).map(s => s.id));
        } else {
            setEditingSupplier(null);
            setSupplierFormData({ name: '', logo_url: '' });
            setSelectedSystemsForSupplier([]);
        }
        setSupplierFormError('');
        setLogoValid(null);
        setSupplierSystemsFilter('');
        setExpandedSupplierSystems({});
        setIsSupplierModalOpen(true);
    };

    const toggleSystemForSupplier = (systemId) => {
        setSelectedSystemsForSupplier(prev =>
            prev.includes(systemId)
                ? prev.filter(id => id !== systemId)
                : [...prev, systemId]
        );
    };

    // Group events by name
    const groupEventsByName = (events) => {
        return events.reduce((acc, event) => {
            const key = event.name;
            if (!acc[key]) {
                acc[key] = [];
            }
            acc[key].push(event);
            return acc;
        }, {});
    };

    // Toggle event group expansion
    const toggleEventGroupExpansion = (groupName) => {
        setExpandedEventGroups(prev => ({
            ...prev,
            [groupName]: !prev[groupName]
        }));
    };

    // Group systems by system type
    const groupSystemsByType = (systems) => {
        return systems.reduce((acc, system) => {
            const key = system.system_type ? system.system_type.id : 'no-type';
            const typeName = system.system_type ? system.system_type.name : 'Sem Tipo';
            if (!acc[key]) {
                acc[key] = { name: typeName, systems: [] };
            }
            acc[key].systems.push(system);
            return acc;
        }, {});
    };

    // Toggle system type expansion
    const toggleSystemTypeExpansion = (typeId) => {
        setExpandedSupplierSystems(prev => ({
            ...prev,
            [typeId]: !prev[typeId]
        }));
    };

    // Group systems by supplier
    const groupSystemsBySupplier = (systems) => {
        return systems.reduce((acc, system) => {
            const key = system.supplier ? system.supplier.id : 'no-supplier';
            const supplierName = system.supplier ? system.supplier.name : 'Sem Fornecedor';
            if (!acc[key]) {
                acc[key] = { name: supplierName, systems: [] };
            }
            acc[key].systems.push(system);
            return acc;
        }, {});
    };

    // Toggle supplier expansion in systems tab
    const toggleSystemSupplierExpansion = (supplierId) => {
        setExpandedSystemsSuppliers(prev => ({
            ...prev,
            [supplierId]: !prev[supplierId]
        }));
    };

    return (
        <div className="flex-1 overflow-auto">
            <div className="p-8">
                <div className="flex items-center gap-3 mb-8">
                    <span className="material-icons text-amber-500 dark:text-amber-400" style={{fontSize: '40px'}}>settings</span>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Cadastros</h1>
                        <p className="text-sm text-gray-600 dark:text-gray-400">Gerenciar cadastros do sistema</p>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700 px-6 pt-6 pb-0">
                        <button
                            onClick={() => { setActiveSection('events'); setSearchTerm(''); setEventsPage(1); }}
                            className={`px-4 py-3 font-medium rounded-t-lg transition ${
                                activeSection === 'events'
                                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-b-2 border-blue-500'
                                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/30'
                            }`}
                        >
                            Eventos
                        </button>
                        <button
                            onClick={() => { setActiveSection('systemtypes'); setSearchTerm(''); setSystemTypesPage(1); }}
                            className={`px-4 py-3 font-medium rounded-t-lg transition ${
                                activeSection === 'systemtypes'
                                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-b-2 border-blue-500'
                                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/30'
                            }`}
                        >
                            Tipo de Sistema
                        </button>
                        <button
                            onClick={() => { setActiveSection('systems'); setSearchTerm(''); setSystemsPage(1); }}
                            className={`px-4 py-3 font-medium rounded-t-lg transition ${
                                activeSection === 'systems'
                                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-b-2 border-blue-500'
                                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/30'
                            }`}
                        >
                            Sistemas
                        </button>
                        <button
                            onClick={() => { setActiveSection('suppliers'); setSearchTerm(''); setSuppliersPage(1); }}
                            className={`px-4 py-3 font-medium rounded-t-lg transition ${
                                activeSection === 'suppliers'
                                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-b-2 border-blue-500'
                                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/30'
                            }`}
                        >
                            Fornecedores
                        </button>
                        <button
                            onClick={() => { setActiveSection('environment'); setSearchTerm(''); }}
                            className={`px-4 py-3 font-medium rounded-t-lg transition ${
                                activeSection === 'environment'
                                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-b-2 border-blue-500'
                                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/30'
                            }`}
                        >
                            Ambiente
                        </button>
                    </div>

                    {activeSection === 'events' && (
                    <>
                    <div className="border-b border-gray-200 dark:border-gray-700 p-6">
                        <h2 className="text-xl font-semibold">Eventos</h2>
                        <p className="text-gray-600 dark:text-gray-400 text-sm mt-2">Gerencie os eventos e webhooks da plataforma</p>
                    </div>

                    <div className="p-6">
                        <div className="flex gap-4 mb-6 items-center">
                            <input
                                type="text"
                                placeholder="Pesquisar evento..."
                                value={searchTerm}
                                onChange={(e) => { setSearchTerm(e.target.value); setEventsPage(1); }}
                                className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <button
                                onClick={() => openEventModal()}
                                className="flex items-center gap-2 px-6 py-2 bg-white dark:bg-gray-800 border border-blue-500 hover:border-blue-600 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 dark:border-blue-400 font-medium transition"
                            >
                                <span className="material-icons sm">add</span>
                                Adicionar Evento
                            </button>
                        </div>

                        {filteredEvents.length === 0 ? (
                            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                                {searchTerm ? 'Nenhum evento encontrado' : 'Nenhum evento cadastrado'}
                            </div>
                        ) : (
                            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                                {(() => {
                                    const groupedEvents = groupEventsByName(filteredEvents);
                                    const groupNames = Object.keys(groupedEvents).sort();

                                    return groupNames.map(groupName => {
                                        const events = groupedEvents[groupName];
                                        const isExpanded = expandedEventGroups[groupName];

                                        return (
                                            <div key={groupName} className={`border-b border-gray-200 dark:border-gray-700 last:border-b-0`}>
                                                {/* Acordeão Header */}
                                                <div className="bg-gray-100 dark:bg-gray-700/50">
                                                    <button
                                                        onClick={() => toggleEventGroupExpansion(groupName)}
                                                        className="w-full flex items-start justify-between gap-3 px-6 py-3 text-left hover:bg-gray-200 dark:hover:bg-gray-700 transition"
                                                    >
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-start gap-2">
                                                                <span className="material-icons text-gray-400 dark:text-gray-500 transition flex-shrink-0 mt-0.5" style={{fontSize: '18px'}}>
                                                                    {isExpanded ? 'expand_less' : 'expand_more'}
                                                                </span>
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="font-semibold text-sm text-gray-900 dark:text-gray-100">{groupName}</p>
                                                                    <div className="mt-1">
                                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                                                            {events.length} evento{events.length !== 1 ? 's' : ''}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </button>

                                                    {/* Conteúdo expandido */}
                                                    {isExpanded && (
                                                        <div className="border-t border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800/50">
                                                            {events.map((event, index) => (
                                                                <div
                                                                    key={event.id}
                                                                    className={`flex items-start justify-between gap-3 px-6 py-3 ${
                                                                        index !== events.length - 1 ? 'border-b border-gray-100 dark:border-gray-700' : ''
                                                                    }`}
                                                                >
                                                                    <div className="flex-1 min-w-0">
                                                                        <div className="flex items-center gap-2">
                                                                            <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium flex-shrink-0 ${
                                                                                event.active
                                                                                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                                                                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400'
                                                                            }`}>
                                                                                {event.type}
                                                                            </span>
                                                                        </div>
                                                                        <p className="text-xs text-gray-400 dark:text-gray-500 font-mono mt-1">
                                                                            {event.id}
                                                                        </p>
                                                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                                            Source: {event.source}
                                                                        </p>
                                                                    </div>
                                                                    <div className="flex gap-1.5 flex-shrink-0">
                                                                        <button
                                                                            onClick={() => openEventModal(event)}
                                                                            className="inline-flex items-center justify-center p-0.5 bg-white dark:bg-gray-800 border border-blue-500 hover:border-blue-600 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 dark:border-blue-400 transition"
                                                                            title="Editar"
                                                                        >
                                                                            <span className="material-icons sm">edit</span>
                                                                        </button>
                                                                        <button
                                                                            onClick={() => handleRemoveEvent(event.id)}
                                                                            className="inline-flex items-center justify-center p-0.5 bg-white dark:bg-gray-800 border border-red-500 hover:border-red-600 text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 dark:border-red-400 transition"
                                                                            title="Remover"
                                                                        >
                                                                            <span className="material-icons sm">delete</span>
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    });
                                })()}
                            </div>
                        )}
                    </div>
                    </>
                    )}

                    {activeSection === 'systemtypes' && (
                    <>
                    <div className="border-b border-gray-200 dark:border-gray-700 p-6">
                        <h2 className="text-xl font-semibold">Tipo de Sistema</h2>
                        <p className="text-gray-600 dark:text-gray-400 text-sm mt-2">Gerencie os tipos de sistema da plataforma</p>
                    </div>

                    <div className="p-6">
                        <div className="flex gap-4 mb-6 items-center">
                            <input
                                type="text"
                                placeholder="Pesquisar tipo de sistema..."
                                value={searchTerm}
                                onChange={(e) => { setSearchTerm(e.target.value); setSystemTypesPage(1); }}
                                className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <button
                                onClick={() => openSystemTypeModal()}
                                className="flex items-center gap-2 px-6 py-2 bg-white dark:bg-gray-800 border border-blue-500 hover:border-blue-600 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 dark:border-blue-400 font-medium transition"
                            >
                                <span className="material-icons sm">add</span>
                                Novo Tipo
                            </button>
                        </div>

                        <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                            <table className="w-full">
                                <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">ID</th>
                                        <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">Nome</th>
                                        <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">Criado em</th>
                                        <th className="px-6 py-3 text-center text-sm font-semibold text-gray-900 dark:text-gray-100 w-32"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {filteredSystemTypes.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                                                {searchTerm ? 'Nenhum tipo de sistema encontrado' : 'Nenhum tipo de sistema cadastrado'}
                                            </td>
                                        </tr>
                                    ) : (
                                        paginatedSystemTypes.map(systemType => (
                                            <tr key={systemType.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                                <td className="px-6 py-4">
                                                    <span className="text-xs font-mono text-gray-600 dark:text-gray-400">{String(systemType.id).slice(0, 8)}...</span>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">{systemType.name}</td>
                                                <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                                                    {new Date(systemType.created_at).toLocaleDateString('pt-BR')}
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <div className="flex gap-2 justify-center">
                                                        <button
                                                            onClick={() => openSystemTypeModal(systemType)}
                                                            className="inline-flex items-center justify-center p-0.5 bg-white dark:bg-gray-800 border border-blue-500 hover:border-blue-600 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 dark:border-blue-400 transition"
                                                            title="Editar"
                                                        >
                                                            <span className="material-icons sm">edit</span>
                                                        </button>
                                                        <button
                                                            onClick={() => handleRemoveSystemType(systemType.id)}
                                                            className="inline-flex items-center justify-center p-0.5 bg-white dark:bg-gray-800 border border-red-500 hover:border-red-600 text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 dark:border-red-400 transition"
                                                            title="Remover"
                                                        >
                                                            <span className="material-icons sm">delete</span>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <Pagination
                            currentPage={systemTypesPage}
                            totalPages={Math.ceil(filteredSystemTypes.length / PAGE_SIZE)}
                            totalItems={filteredSystemTypes.length}
                            itemsPerPage={PAGE_SIZE}
                            onPageChange={setSystemTypesPage}
                        />
                    </div>
                    </>
                    )}

                    {activeSection === 'systems' && (
                    <>
                    <div className="border-b border-gray-200 dark:border-gray-700 p-6">
                        <h2 className="text-xl font-semibold">Sistemas</h2>
                        <p className="text-gray-600 dark:text-gray-400 text-sm mt-2">Gerencie os sistemas da plataforma</p>
                    </div>

                    <div className="p-6">
                        <div className="flex gap-4 mb-6 items-center">
                            <input
                                type="text"
                                placeholder="Pesquisar sistema..."
                                value={searchTerm}
                                onChange={(e) => { setSearchTerm(e.target.value); setSystemsPage(1); }}
                                className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <button
                                onClick={() => openSystemModal()}
                                className="flex items-center gap-2 px-6 py-2 bg-white dark:bg-gray-800 border border-blue-500 hover:border-blue-600 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 dark:border-blue-400 font-medium transition"
                            >
                                <span className="material-icons sm">add</span>
                                Novo Sistema
                            </button>
                        </div>

                        <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
                            {filteredSystems.length === 0 ? (
                                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                                    {searchTerm ? 'Nenhum sistema encontrado' : 'Nenhum sistema cadastrado'}
                                </div>
                            ) : (
                                <>
                                    {(() => {
                                        const groupedSystems = groupSystemsBySupplier(paginatedSystems);
                                        const supplierKeys = Object.keys(groupedSystems).sort();

                                        return supplierKeys.map((supplierId, idx) => {
                                            const supplier = groupedSystems[supplierId];
                                            const isExpanded = expandedSystemsSuppliers[supplierId];

                                            return (
                                                <div key={supplierId} className={`border-b border-gray-200 dark:border-gray-700 last:border-b-0`}>
                                                    {/* Acordeão Header */}
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleSystemSupplierExpansion(supplierId)}
                                                        className="w-full flex items-center justify-between gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition text-left"
                                                    >
                                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                                            <span className="material-icons text-gray-400 dark:text-gray-500 transition flex-shrink-0" style={{fontSize: '18px'}}>
                                                                {isExpanded ? 'expand_less' : 'expand_more'}
                                                            </span>
                                                            {(() => {
                                                                const supplierData = suppliersData.find(s => s.id === supplierId);
                                                                return (
                                                                    <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded flex items-center justify-center flex-shrink-0">
                                                                        {supplierData?.logo_url ? (
                                                                            <img src={supplierData.logo_url} alt={supplier.name} className="w-full h-full object-contain p-1 rounded" />
                                                                        ) : (
                                                                            <span className="material-icons text-gray-400 dark:text-gray-500" style={{fontSize: '16px'}}>image</span>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })()}
                                                            <p className="font-semibold text-sm text-gray-900 dark:text-gray-100">{supplier.name}</p>
                                                        </div>
                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 flex-shrink-0">
                                                            {supplier.systems.length} sistema{supplier.systems.length !== 1 ? 's' : ''}
                                                        </span>
                                                    </button>

                                                    {/* Conteúdo expandido */}
                                                    {isExpanded && (
                                                        <div className="bg-white dark:bg-gray-800/50 divide-y divide-gray-200 dark:divide-gray-700">
                                                            {supplier.systems.map((system) => (
                                                                <div key={system.id} className="flex items-start justify-between gap-3 p-4">
                                                                    <div className="flex-1 min-w-0">
                                                                        <p className="text-sm text-gray-900 dark:text-gray-100 font-medium">{system.name || system.description}</p>
                                                                        <p className="text-xs font-mono text-gray-500 dark:text-gray-400 mt-1">{system.id}</p>
                                                                        <div className="flex flex-wrap gap-2 mt-2">
                                                                            {system.system_type && (
                                                                                <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300">
                                                                                    {system.system_type.name}
                                                                                </span>
                                                                            )}
                                                                            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                                                                {new Date(system.created_at).toLocaleDateString('pt-BR')}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex gap-2 flex-shrink-0">
                                                                        <button
                                                                            onClick={() => openSystemModal(system)}
                                                                            className="inline-flex items-center justify-center p-0.5 bg-white dark:bg-gray-800 border border-blue-500 hover:border-blue-600 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 dark:border-blue-400 transition"
                                                                            title="Editar"
                                                                        >
                                                                            <span className="material-icons sm">edit</span>
                                                                        </button>
                                                                        <button
                                                                            onClick={() => handleRemoveSystem(system.id)}
                                                                            className="inline-flex items-center justify-center p-0.5 bg-white dark:bg-gray-800 border border-red-500 hover:border-red-600 text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 dark:border-red-400 transition"
                                                                            title="Remover"
                                                                        >
                                                                            <span className="material-icons sm">delete</span>
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        });
                                    })()}
                                    <Pagination
                                        currentPage={systemsPage}
                                        totalPages={Math.ceil(filteredSystems.length / PAGE_SIZE)}
                                        totalItems={filteredSystems.length}
                                        itemsPerPage={PAGE_SIZE}
                                        onPageChange={setSystemsPage}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                    </>
                    )}

                    {activeSection === 'suppliers' && (
                    <>
                    <div className="border-b border-gray-200 dark:border-gray-700 p-6">
                        <h2 className="text-xl font-semibold">Fornecedores</h2>
                        <p className="text-gray-600 dark:text-gray-400 text-sm mt-2">Gerencie os fornecedores e seus sistemas</p>
                    </div>

                    <div className="p-6">
                        <div className="flex gap-4 mb-6 items-center">
                            <input
                                type="text"
                                placeholder="Pesquisar fornecedor..."
                                value={searchTerm}
                                onChange={(e) => { setSearchTerm(e.target.value); setSuppliersPage(1); }}
                                className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <button
                                onClick={() => openSupplierModal()}
                                className="flex items-center gap-2 px-6 py-2 bg-white dark:bg-gray-800 border border-blue-500 hover:border-blue-600 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 dark:border-blue-400 font-medium transition"
                            >
                                <span className="material-icons sm">add</span>
                                Novo Fornecedor
                            </button>
                        </div>

                        <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                            <table className="w-full">
                                <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100 w-12"></th>
                                        <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">Nome</th>
                                        <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">Sistemas</th>
                                        <th className="px-6 py-3 text-center text-sm font-semibold text-gray-900 dark:text-gray-100 w-32"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {filteredSuppliers.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                                                {searchTerm ? 'Nenhum fornecedor encontrado' : 'Nenhum fornecedor cadastrado'}
                                            </td>
                                        </tr>
                                    ) : (
                                        paginatedSuppliers.map(supplier => (
                                            <tr key={supplier.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                                <td className="px-6 py-4">
                                                    <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded flex items-center justify-center flex-shrink-0">
                                                        {supplier.logo_url ? (
                                                            <img src={supplier.logo_url} alt={supplier.name} className="w-full h-full object-contain p-1 rounded" />
                                                        ) : (
                                                            <span className="material-icons text-gray-400 dark:text-gray-500" style={{fontSize: '18px'}}>image</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <p className="text-sm text-gray-900 dark:text-gray-100">{supplier.name}</p>
                                                    <p className="text-xs font-mono text-gray-500 dark:text-gray-400 mt-1">{supplier.id}</p>
                                                </td>
                                                <td className="px-6 py-4 text-sm">
                                                    {supplier.systems && supplier.systems.length > 0 ? (
                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                                                            {supplier.systems.length} sistema{supplier.systems.length !== 1 ? 's' : ''}
                                                        </span>
                                                    ) : (
                                                        <span className="text-gray-500 dark:text-gray-400 text-xs">Nenhum</span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 text-center">
                                                    <div className="flex gap-2 justify-center">
                                                        <button
                                                            onClick={() => openSupplierModal(supplier)}
                                                            className="inline-flex items-center justify-center p-0.5 bg-white dark:bg-gray-800 border border-blue-500 hover:border-blue-600 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 dark:border-blue-400 transition"
                                                            title="Editar"
                                                        >
                                                            <span className="material-icons sm">edit</span>
                                                        </button>
                                                        <button
                                                            onClick={() => handleRemoveSupplier(supplier.id)}
                                                            className="inline-flex items-center justify-center p-0.5 bg-white dark:bg-gray-800 border border-red-500 hover:border-red-600 text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 dark:border-red-400 transition"
                                                            title="Remover"
                                                        >
                                                            <span className="material-icons sm">delete</span>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <Pagination
                            currentPage={suppliersPage}
                            totalPages={Math.ceil(filteredSuppliers.length / PAGE_SIZE)}
                            totalItems={filteredSuppliers.length}
                            itemsPerPage={PAGE_SIZE}
                            onPageChange={setSuppliersPage}
                        />
                    </div>
                    </>
                    )}

                    {activeSection === 'environment' && (
                    <>
                    <EnvironmentSection token={localStorage.getItem('token')} />
                    </>
                    )}
                </div>

                {isEventModalOpen && (
                    <Fragment>
                        <div className="fixed inset-0 bg-black/50 z-30" onClick={() => setIsEventModalOpen(false)}></div>
                        <div className="fixed right-0 top-0 h-screen w-full max-w-md bg-white dark:bg-gray-800 shadow-lg z-40 overflow-y-auto">
                            <div className="p-6">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-xl font-semibold">{editingEvent ? 'Editar Evento' : 'Novo Evento'}</h2>
                                    <button
                                        onClick={() => setIsEventModalOpen(false)}
                                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
                                    >
                                        <span className="material-icons sm">close</span>
                                    </button>
                                </div>

                                <form onSubmit={handleAddEvent} className="space-y-4">
                                    {eventFormError && (
                                        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
                                            {eventFormError}
                                        </div>
                                    )}
                                    {editingEvent && (
                                        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-blue-700 dark:text-blue-400 text-sm">
                                            ID: {editingEvent.id}
                                        </div>
                                    )}
                                    <div>
                                        <label className="block text-sm font-medium mb-2">Nome do Evento</label>
                                        <input
                                            type="text"
                                            value={eventFormData.name}
                                            onChange={(e) => { setEventFormData({ ...eventFormData, name: e.target.value }); setEventFormError(''); }}
                                            placeholder="Ex: Worklist"
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                    {editingEvent ? (
                                        <div>
                                            <label className="block text-sm font-medium mb-2">Type (CloudEvents)</label>
                                            <input
                                                type="text"
                                                value={eventFormData.type}
                                                onChange={(e) => { setEventFormData({ ...eventFormData, type: e.target.value }); setEventFormError(''); }}
                                                placeholder="Ex: wl.created"
                                                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                    ) : (
                                        <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
                                            <div className="bg-gray-50 dark:bg-gray-700/50 px-4 py-3 border-b border-gray-300 dark:border-gray-600">
                                                <label className="block text-sm font-medium mb-3">Types (CloudEvents)</label>
                                                <div className="flex gap-2">
                                                    <input
                                                        type="text"
                                                        value={currentEventType}
                                                        onChange={(e) => { setCurrentEventType(e.target.value); setEventFormError(''); }}
                                                        onKeyPress={(e) => { if (e.key === 'Enter') { addEventType(); e.preventDefault(); } }}
                                                        placeholder="Ex: wl.created"
                                                        className="flex-1 h-10 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={addEventType}
                                                        className="px-4 py-2 bg-white dark:bg-gray-800 border border-blue-500 hover:border-blue-600 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 dark:border-blue-400 font-medium transition flex items-center justify-center"
                                                    >
                                                        <span className="material-icons sm">add</span>
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="h-48 overflow-y-auto bg-white dark:bg-gray-800">
                                                {eventTypes.length === 0 ? (
                                                    <div className="h-full flex flex-col items-center justify-center text-gray-400 dark:text-gray-500 text-sm gap-2">
                                                        <span className="material-icons" style={{fontSize: '36px'}}>inbox</span>
                                                        <span>Nenhum tipo adicionado</span>
                                                    </div>
                                                ) : (
                                                    <div className="divide-y divide-gray-200 dark:divide-gray-700">
                                                        {eventTypes.map((type, index) => (
                                                            <div key={index} className="flex items-center justify-between gap-2 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition">
                                                                <span className="text-sm text-gray-900 dark:text-gray-100">{type}</span>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeEventType(index)}
                                                                    className="p-1 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition"
                                                                    title="Remover tipo"
                                                                >
                                                                    <span className="material-icons sm">delete</span>
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    <div>
                                        <label className="block text-sm font-medium mb-2">Source (CloudEvents)</label>
                                        <input
                                            type="text"
                                            value={eventFormData.source}
                                            onChange={(e) => { setEventFormData({ ...eventFormData, source: e.target.value }); setEventFormError(''); }}
                                            placeholder="Ex: api/v1"
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-sm font-medium mb-2">Spec Version</label>
                                            <input
                                                type="text"
                                                value={eventFormData.specversion}
                                                onChange={(e) => { setEventFormData({ ...eventFormData, specversion: e.target.value }); setEventFormError(''); }}
                                                placeholder="1.0"
                                                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-2">Content Type</label>
                                            <input
                                                type="text"
                                                value={eventFormData.datacontenttype}
                                                onChange={(e) => { setEventFormData({ ...eventFormData, datacontenttype: e.target.value }); setEventFormError(''); }}
                                                placeholder="application/json"
                                                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 pt-2">
                                        <input
                                            type="checkbox"
                                            id="eventActive"
                                            checked={eventFormData.active}
                                            onChange={(e) => setEventFormData({ ...eventFormData, active: e.target.checked })}
                                            className="w-4 h-4 rounded border-gray-300 focus:ring-2 focus:ring-blue-500"
                                        />
                                        <label htmlFor="eventActive" className="text-sm font-medium">
                                            Evento Ativo
                                        </label>
                                    </div>

                                    <div className="flex gap-2 pt-6">
                                        <button
                                            type="submit"
                                            className="flex-1 px-4 py-2 bg-white dark:bg-gray-800 border border-blue-500 hover:border-blue-600 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 dark:border-blue-400 font-medium transition"
                                        >
                                            {editingEvent ? 'Atualizar' : 'Criar Eventos'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setIsEventModalOpen(false)}
                                            className="flex-1 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-900 dark:text-gray-100 font-medium transition"
                                        >
                                            Cancelar
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </Fragment>
                )}

                {isSystemTypeModalOpen && (
                    <Fragment>
                        <div className="fixed inset-0 bg-black/50 z-30" onClick={() => setIsSystemTypeModalOpen(false)}></div>
                        <div className="fixed right-0 top-0 h-screen w-full max-w-md bg-white dark:bg-gray-800 shadow-lg z-40 overflow-y-auto">
                            <div className="p-6">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-xl font-semibold">{editingSystemType ? 'Editar Tipo de Sistema' : 'Novo Tipo de Sistema'}</h2>
                                    <button
                                        onClick={() => setIsSystemTypeModalOpen(false)}
                                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
                                    >
                                        <span className="material-icons sm">close</span>
                                    </button>
                                </div>

                                <form onSubmit={handleAddSystemType} className="space-y-4">
                                    {systemTypeFormError && (
                                        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
                                            {systemTypeFormError}
                                        </div>
                                    )}
                                    {editingSystemType ? (
                                        <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400 text-sm">
                                            <span className="material-icons sm inline mr-2" style={{fontSize: '16px'}}>check_circle</span>
                                            ID: <span className="font-mono">{editingSystemType.id}</span>
                                        </div>
                                    ) : (
                                        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-blue-700 dark:text-blue-400 text-sm">
                                            <span className="material-icons sm inline mr-2" style={{fontSize: '16px'}}>info</span>
                                            Um ID aleatório será gerado automaticamente
                                        </div>
                                    )}
                                    <div>
                                        <label className="block text-sm font-medium mb-2">Nome do Tipo</label>
                                        <input
                                            type="text"
                                            value={systemTypeFormData.name}
                                            onChange={(e) => { setSystemTypeFormData({ ...systemTypeFormData, name: e.target.value }); setSystemTypeFormError(''); }}
                                            placeholder="Ex: Sistema Administrativo"
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div className="flex gap-2 pt-4">
                                        <button
                                            type="submit"
                                            className="flex-1 px-4 py-2 bg-white dark:bg-gray-800 border border-blue-500 hover:border-blue-600 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 dark:border-blue-400 font-medium transition"
                                        >
                                            {editingSystemType ? 'Atualizar' : 'Criar'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setIsSystemTypeModalOpen(false)}
                                            className="flex-1 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-900 dark:text-gray-100 font-medium transition"
                                        >
                                            Cancelar
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </Fragment>
                )}

                {isSystemModalOpen && (
                    <Fragment>
                        <div className="fixed inset-0 bg-black/50 z-30" onClick={() => setIsSystemModalOpen(false)}></div>
                        <div className="fixed right-0 top-0 h-screen w-full max-w-md bg-white dark:bg-gray-800 shadow-lg z-40 overflow-y-auto">
                            <div className="p-6">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-xl font-semibold">{editingSystem ? 'Editar Sistema' : 'Novo Sistema'}</h2>
                                    <button
                                        onClick={() => setIsSystemModalOpen(false)}
                                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
                                    >
                                        <span className="material-icons sm">close</span>
                                    </button>
                                </div>

                                <form onSubmit={handleAddSystem} className="space-y-4">
                                    {systemFormError && (
                                        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
                                            {systemFormError}
                                        </div>
                                    )}
                                    {editingSystem ? (
                                        <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400 text-sm">
                                            <span className="material-icons sm inline mr-2" style={{fontSize: '16px'}}>check_circle</span>
                                            ID: <span className="font-mono">{editingSystem.id}</span>
                                        </div>
                                    ) : (
                                        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-blue-700 dark:text-blue-400 text-sm">
                                            <span className="material-icons sm inline mr-2" style={{fontSize: '16px'}}>info</span>
                                            Um ID aleatório será gerado automaticamente
                                        </div>
                                    )}
                                    <div>
                                        <label className="block text-sm font-medium mb-2">Nome do Sistema <span className="text-red-500">*</span></label>
                                        <input
                                            type="text"
                                            value={systemFormData.name}
                                            onChange={(e) => { setSystemFormData({ ...systemFormData, name: e.target.value }); setSystemFormError(''); }}
                                            placeholder="Ex: Sistema de Agendamento"
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-2">Descrição <span className="text-red-500">*</span></label>
                                        <textarea
                                            value={systemFormData.description}
                                            onChange={(e) => { setSystemFormData({ ...systemFormData, description: e.target.value }); setSystemFormError(''); }}
                                            placeholder="Descreva detalhes sobre o sistema"
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            rows="3"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-2">Tipo de Sistema</label>
                                        <select
                                            value={systemFormData.system_type_id || ''}
                                            onChange={(e) => setSystemFormData({ ...systemFormData, system_type_id: e.target.value ? e.target.value : null })}
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="">Selecione um tipo...</option>
                                            {systemTypesData.map(type => (
                                                <option key={type.id} value={type.id}>
                                                    {type.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-2">Fornecedor <span className="text-red-500">*</span></label>
                                        <select
                                            value={systemFormData.supplier_id || ''}
                                            onChange={(e) => { setSystemFormData({ ...systemFormData, supplier_id: e.target.value ? e.target.value : null }); setSystemFormError(''); }}
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="">Selecione um fornecedor...</option>
                                            {suppliersData.map(supplier => (
                                                <option key={supplier.id} value={supplier.id}>
                                                    {supplier.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="flex gap-2 pt-4">
                                        <button
                                            type="submit"
                                            className="flex-1 px-4 py-2 bg-white dark:bg-gray-800 border border-blue-500 hover:border-blue-600 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 dark:border-blue-400 font-medium transition"
                                        >
                                            {editingSystem ? 'Atualizar' : 'Criar'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setIsSystemModalOpen(false)}
                                            className="flex-1 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-900 dark:text-gray-100 font-medium transition"
                                        >
                                            Cancelar
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </Fragment>
                )}

                {isSupplierModalOpen && (
                    <Fragment>
                        <div className="fixed inset-0 bg-black/50 z-30" onClick={() => setIsSupplierModalOpen(false)}></div>
                        <div className="fixed right-0 top-0 h-screen w-full max-w-2xl bg-white dark:bg-gray-800 shadow-lg z-40 overflow-y-auto">
                            <div className="p-6">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-xl font-semibold">{editingSupplier ? 'Editar' : 'Novo'} Fornecedor</h2>
                                    <button
                                        onClick={() => setIsSupplierModalOpen(false)}
                                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
                                    >
                                        <span className="material-icons sm">close</span>
                                    </button>
                                </div>

                                <form onSubmit={handleAddSupplier} className="space-y-6">
                                    {supplierFormError && (
                                        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
                                            {supplierFormError}
                                        </div>
                                    )}

                                    <div>
                                        <label className="block text-sm font-medium mb-2">Nome do Fornecedor</label>
                                        <input
                                            type="text"
                                            value={supplierFormData.name}
                                            onChange={(e) => { setSupplierFormData({ ...supplierFormData, name: e.target.value }); setSupplierFormError(''); }}
                                            placeholder="Ex: Acme Systems"
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium mb-2">URL do Logo</label>
                                        <input
                                            type="text"
                                            value={supplierFormData.logo_url}
                                            onChange={(e) => { setSupplierFormData({ ...supplierFormData, logo_url: e.target.value }); }}
                                            placeholder="https://example.com/logo.png"
                                            className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>

                                    {supplierFormData.logo_url && (
                                        <div>
                                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Preview do Logo</p>
                                            <div className="w-full h-32 bg-gray-100 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 flex items-center justify-center overflow-hidden">
                                                <img
                                                    src={supplierFormData.logo_url}
                                                    alt="Logo Preview"
                                                    className="h-full w-full object-contain p-2"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        if (!e.target.nextElementSibling) {
                                                            const errorDiv = document.createElement('div');
                                                            errorDiv.className = 'flex flex-col items-center justify-center gap-2 text-gray-400 dark:text-gray-500';
                                                            errorDiv.innerHTML = '<span class="material-icons lg">broken_image</span><span class="text-xs">Imagem inválida</span>';
                                                            e.target.parentElement.appendChild(errorDiv);
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {editingSupplier && (
                                        <div>
                                            <label className="block text-sm font-medium mb-3">Sistemas Associados</label>
                                            {(!editingSupplier.systems || editingSupplier.systems.length === 0) ? (
                                                <p className="text-sm text-gray-600 dark:text-gray-400">Nenhum sistema associado</p>
                                            ) : (
                                                <div className="border border-gray-200 dark:border-gray-600 rounded-lg">
                                                    {(() => {
                                                        const systemsToShow = editingSupplier.systems || [];
                                                        const groupedSystems = groupSystemsByType(systemsToShow);
                                                        const typeKeys = Object.keys(groupedSystems).sort();

                                                        return typeKeys.map((typeId, idx) => {
                                                            const systemType = groupedSystems[typeId];
                                                            const isExpanded = expandedSupplierSystems[typeId];

                                                            return (
                                                                <div key={typeId} className={`border-b border-gray-200 dark:border-gray-600 last:border-b-0`}>
                                                                    {/* Acordeão Header */}
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => toggleSystemTypeExpansion(typeId)}
                                                                        className="w-full flex items-center justify-between gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition text-left"
                                                                    >
                                                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                                                            <span className="material-icons text-gray-400 dark:text-gray-500 transition flex-shrink-0" style={{fontSize: '18px'}}>
                                                                                {isExpanded ? 'expand_less' : 'expand_more'}
                                                                            </span>
                                                                            <p className="font-semibold text-sm text-gray-900 dark:text-gray-100">{systemType.name}</p>
                                                                        </div>
                                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 flex-shrink-0">
                                                                            {systemType.systems.length} sistema{systemType.systems.length !== 1 ? 's' : ''}
                                                                        </span>
                                                                    </button>

                                                                    {/* Conteúdo expandido */}
                                                                    {isExpanded && (
                                                                        <div className="bg-white dark:bg-gray-800/50 divide-y divide-gray-200 dark:divide-gray-600">
                                                                            {systemType.systems.map((system) => (
                                                                                <div key={system.id} className="flex items-start gap-3 p-3">
                                                                                    <div className="flex-1 min-w-0">
                                                                                        <p className="text-sm text-gray-900 dark:text-gray-100 font-medium">{system.name || system.description}</p>
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        });
                                                    })()}
                                                </div>
                                            )}
                                        </div>
                                    )}


                                    <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                                        <button
                                            type="submit"
                                            className="flex-1 px-4 py-2 bg-white dark:bg-gray-800 border border-blue-500 hover:border-blue-600 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 dark:border-blue-400 font-medium transition"
                                        >
                                            {editingSupplier ? 'Atualizar' : 'Criar'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setIsSupplierModalOpen(false)}
                                            className="flex-1 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-900 dark:text-gray-100 font-medium transition"
                                        >
                                            Cancelar
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </Fragment>
                )}
            </div>
        </div>
    );
};

// ============ SQL Admin Page ============

export default SettingsPage
